import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Car, Wrench, CalendarClock, AlertTriangle, CheckSquare, Package, Plus, History, Receipt } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useSession } from "@/context/SessionContext";
import { useFetch } from "@/lib/useFetch";
import { fmtINR, fmtDate } from "@/lib/format";
import { ErrorState, LoadingRows, EmptyState } from "@/components/common/States";
import { NewServiceDialog } from "./ServiceRecords";
import { StatusBadge } from "./shared";

const monthStart = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; };
const today = () => new Date().toISOString().slice(0, 10);
const EDITABLE = ["draft", "approved", "in_progress"];

export const RecordExpenseDialog = ({ open, onOpenChange }) => {
  const nav = useNavigate();
  const { data, loading } = useFetch("/service-records", { status: "open", limit: 100 }, open);
  const rows = (data?.items || []).filter((r) => EDITABLE.includes(r.status));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="record-expense-dialog">
        <DialogHeader><DialogTitle>Record expense</DialogTitle><DialogDescription>Choose the open service to add labour, parts or garage bill costs.</DialogDescription></DialogHeader>
        {loading && <LoadingRows rows={3} />}
        {data && !rows.length && <EmptyState title="No open services" body="Start a new service first, then add its costs." testId="record-expense-empty" />}
        <div className="max-h-[50vh] overflow-y-auto divide-y border rounded-md">
          {rows.map((r) => (
            <button key={r.id} className="w-full text-left px-3 py-2.5 hover:bg-secondary flex items-center gap-3" onClick={() => nav(`/service-records/${r.id}?edit=1`)} data-testid={`record-expense-pick-${r.id}`}>
              <span className="font-mono-g font-semibold text-sm">{r.registration_number}</span>
              <span className="text-xs text-muted-foreground flex-1 truncate">{r.number} · {fmtDate(r.service_date)}</span>
              <StatusBadge s={r.status} />
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const QuickActions = () => {
  const { can } = useSession();
  const nav = useNavigate();
  const [dlg, setDlg] = useState(null);
  const acts = [
    ["vehicle.manage", "Add Vehicle", Car, () => nav("/vehicles?new=1"), "quick-add-vehicle"],
    ["maintenance.manage", "New Service", Plus, () => setDlg("service"), "quick-new-service"],
    ["maintenance.view", "Service History", History, () => nav("/garage-vehicles"), "quick-service-history"],
    ["maintenance.manage", "Record Expense", Receipt, () => setDlg("expense"), "quick-record-expense"],
  ].filter(([p]) => can(p));
  if (!acts.length) return null;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="quick-actions">
      {acts.map(([, l, Icon, fn, id], i) => (
        <Button key={id} variant={i === 1 ? "default" : "outline"} className={`h-12 justify-start gap-2 text-sm ${i === 1 ? "" : "bg-white"}`} onClick={fn} data-testid={id}>
          <Icon className={`h-4 w-4 ${i === 1 ? "" : "text-gold"}`} />{l}
        </Button>
      ))}
      <NewServiceDialog open={dlg === "service"} onOpenChange={() => setDlg(null)} />
      <RecordExpenseDialog open={dlg === "expense"} onOpenChange={() => setDlg(null)} />
    </div>
  );
};

const Tile = ({ icon: Icon, label, value, to, alert, testId }) => (
  <Link to={to} className="g39-card g39-card-hover p-4 block" data-testid={testId}>
    <div className="flex items-center gap-2 text-[13px] text-muted-foreground"><Icon className={`h-4 w-4 ${alert ? "text-red-600" : "text-gold"}`} />{label}</div>
    <div className={`mt-2 font-display text-2xl sm:text-3xl font-bold ${alert ? "text-red-600" : ""}`} data-testid={`${testId}-value`}>{value}</div>
  </Link>
);

export const GarageKpis = () => {
  const [p, setP] = useState({ from: monthStart(), to: today() });
  const { data, loading, error, reload } = useFetch("/garage/dashboard", p);
  const tiles = data ? [
    ["total_vehicles", Car, "Total vehicles", "/garage-vehicles"],
    ["in_service", Wrench, "In service", "/service-records?status=open"],
    ["services_due", CalendarClock, "Service due soon", "/service-timeline"],
    ["overdue", AlertTriangle, "Overdue", "/service-timeline", true],
    ["pending_approvals", CheckSquare, "Pending approvals", "/service-approvals"],
    ["low_stock", Package, "Low-stock parts", "/stock", true],
  ].filter(([k]) => data[k] !== undefined) : [];
  return (
    <section className="mb-6" data-testid="garage-kpis">
      {loading && !data && <div className="g39-card"><LoadingRows rows={3} /></div>}
      {error && <div className="g39-card"><ErrorState message={error} onRetry={reload} /></div>}
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            {tiles.map(([k, icon, label, to, al]) => <Tile key={k} icon={icon} label={label} value={data[k]} to={to} alert={al && data[k] > 0} testId={`kpi-${k}`} />)}
          </div>
          {data.expenditure && (
            <div className="g39-card p-4 mt-3 flex flex-col md:flex-row md:items-center gap-3" data-testid="kpi-expenditure">
              <div className="flex-1">
                <div className="text-[13px] text-muted-foreground">Maintenance spend</div>
                <div className="font-display text-2xl font-bold" data-testid="kpi-expenditure-total">{fmtINR(data.expenditure.total)}</div>
                <div className="text-xs text-muted-foreground">Labour {fmtINR(data.expenditure.labor)} · Parts {fmtINR(data.expenditure.parts)} · External {fmtINR(data.expenditure.external)}</div>
              </div>
              <div className="grid grid-cols-2 gap-2 md:flex">
                <Input type="date" className="min-w-0 w-full md:w-40" value={p.from} onChange={(e) => setP((x) => ({ ...x, from: e.target.value }))} data-testid="dash-from" aria-label="From" />
                <Input type="date" className="min-w-0 w-full md:w-40" value={p.to} onChange={(e) => setP((x) => ({ ...x, to: e.target.value }))} data-testid="dash-to" aria-label="To" />
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
};

const ListCard = ({ title, to, testId, children }) => (
  <div className="g39-card" data-testid={testId}>
    <div className="px-4 py-3 border-b flex items-center justify-between"><span className="font-semibold text-sm">{title}</span><Link to={to} className="text-xs text-gold" data-testid={`${testId}-all`}>View all</Link></div>
    {children}
  </div>
);

export const RecentServices = () => {
  const { data } = useFetch("/service-records", { limit: 6 });
  return (
    <ListCard title="Recent services" to="/service-records" testId="dash-recent-services">
      {data && !data.items.length && <EmptyState title="No services yet" testId="dash-recent-empty" />}
      <div className="divide-y">
        {(data?.items || []).map((r) => (
          <Link key={r.id} to={`/service-records/${r.id}`} className="flex items-center gap-3 px-4 py-2.5 hover:bg-secondary text-sm" data-testid={`dash-service-${r.id}`}>
            <span className="font-mono-g font-semibold">{r.registration_number}</span>
            <span className="flex-1 truncate text-muted-foreground">{r.complaint}</span>
            <StatusBadge s={r.status} />
          </Link>
        ))}
      </div>
    </ListCard>
  );
};

export const LowStock = () => {
  const { data } = useFetch("/stock", { low_only: true, limit: 6 });
  return (
    <ListCard title="Low-stock parts" to="/stock" testId="dash-low-stock">
      {data && !data.items.length && <EmptyState title="All parts above minimum" testId="dash-low-stock-empty" />}
      <div className="divide-y">
        {(data?.items || []).map((p) => (
          <div key={p.id} className="flex items-center gap-3 px-4 py-2.5 text-sm" data-testid={`dash-low-${p.id}`}>
            <span className="flex-1 truncate">{p.name}</span>
            <span className="text-red-600 font-semibold">{p.stock_qty} {p.uom}</span>
            <span className="text-xs text-muted-foreground">min {p.reorder_threshold}</span>
          </div>
        ))}
      </div>
    </ListCard>
  );
};
